import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "placement", "onOpenChange", "isOpen"];
import HR from 'UIComponents/elements/HR';
import { CardMenu, CardMenuFooterLink, CardMenuOptions } from '../components/CardMenu';
import { CardMenuChangePlaylistThumbnailOption } from '../../savedItems/components/CardMenuChangePlaylistThumbnailOption';
import CardMenuAddToPlaylistOption from '../../savedItems/components/CardMenuAddToPlaylistOption';
import CardMenuWatchLaterOption from '../../savedItems/components/CardMenuWatchLaterOption';
import CreatePlaylistLink from '../../savedItems/components/CreatePlaylistLink';
import { EditPlaylistButton } from '../../savedItems/components/EditPlaylistButton';
import { useUserOwnsPlaylist } from '../../savedItems/savedItemsQueries';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISideNavItem from 'UIComponents/nav/UISideNavItem';
import StartPlaylistButton from '../../savedItems/components/StartPlaylistButton';
import styled from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { SocialShareMenuOption } from '../../share';
import { DuplicatePlaylistButton } from '../../savedItems/components/DuplicatePlaylistButton';
import { ArchivePlaylistButton } from '../../myPlaylists/components/ArchivePlaylistButton';
import { SavePlaylistButton } from '../../myPlaylists/components/SavePlaylistButton';
import { AssignPlaylistButton } from '../../playlistAssignments/components/AssignPlaylistButton';
import { CardMenuUnassignPlaylistOption } from '../../playlistAssignments/components/CardMenuUnassignPlaylistOption';
import CopyEmbedCodeMenuOption from '../../share/components/CopyEmbedCodeMenuOption';
import { CreateHubspotPlaylistButton } from '../../hubspotPlaylists/components/createOrEditHubspotPlaylist/CreateHubspotPlaylistButton';
import { useHasScope, useHasGate } from '../../accounts';
import EditHubspotPlaylistButton from '../../hubspotPlaylists/components/createOrEditHubspotPlaylist/EditHubspotPlaylistButton';
import { useCanUserEditPlaylist, useIsHubspotPlaylist } from '../../savedItems/savedItemsHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardStartPlaylistButton = styled(StartPlaylistButton).withConfig({
  displayName: "DesktopCardMenu__CardStartPlaylistButton",
  componentId: "zwhfxe-0"
})([".loading-spinner{position:relative !important;}"]);
const menuItemStyle = {
  textDecoration: 'none',
  fontWeight: 'normal',
  width: '100%'
};
const trackCreateNewPlaylist = (itemId, type) => {
  UsageTracker.track('playlistInteraction', {
    action: 'add-to-new-playlist',
    location: 'card-menu',
    itemId,
    itemType: type
  });
};
const trackAddToPlaylist = (itemId, type) => {
  UsageTracker.track('playlistInteraction', {
    action: 'add-to-playlist',
    location: 'card-menu',
    itemId,
    itemType: type
  });
};
const trackUnassignedPlaylist = () => {
  UsageTracker.track('playlistInteraction', {
    action: 'playlist-unassigned',
    location: 'card-menu'
  });
};
const TrackLessonMenuOptions = ({
  item,
  itemToShare,
  closeModal
}) => {
  const onCreateNewClick = () => {
    trackCreateNewPlaylist(item.id, item.type);
    closeModal();
  };
  const onAddToPlaylistClick = () => {
    trackAddToPlaylist(item.id, item.type);
    closeModal();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardMenuWatchLaterOption, {
      item: item,
      onClick: closeModal
    }), /*#__PURE__*/_jsx(CardMenuAddToPlaylistOption, {
      item: item,
      onCreatePlaylistClick: onCreateNewClick,
      onPlaylistClick: onAddToPlaylistClick
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: itemToShare || item,
      onShare: closeModal,
      shareButtonLabel: itemToShare ? undefined : item.type === 'TRACK' ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.shareThis.shareTrack"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.shareThis.shareLesson"
      })
    }), /*#__PURE__*/_jsx(CopyEmbedCodeMenuOption, {
      language: item.language
    })]
  });
};
const TrackLessonMenuFooter = ({
  item,
  closeModal
}) => {
  const onCreateNewClick = () => {
    closeModal();
    trackCreateNewPlaylist(item.id, item.type);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsx(CreatePlaylistLink, {
      item: item,
      render: (content, to) => /*#__PURE__*/_jsx(CardMenuFooterLink, {
        onClick: onCreateNewClick,
        to: to,
        children: content
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.cardMenu.addToNewPlaylist"
      })
    })]
  });
};
const PlaylistMenuOptions = ({
  item,
  itemToShare,
  closeModal
}) => {
  const {
    id: playlistId
  } = item;
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const userCanEditPlaylist = useCanUserEditPlaylist(item.id);
  const isHubspotPlaylist = useIsHubspotPlaylist(item.id);
  const {
    hasGate: isUngatedToLearningPaths
  } = useHasGate('Academy:LearningPaths');
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  const shouldDisplayHubspotPlaylistButtons = hasPublicPlaylistsAdminScope && isUngatedToLearningPaths;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UISideNavItem, {
      linkComponent: "div",
      title: /*#__PURE__*/_jsx(CardStartPlaylistButton, {
        appearsIn: "playlist",
        playlistId: playlistId,
        showLoader: true,
        style: menuItemStyle,
        use: "unstyled"
      })
    }), userCanEditPlaylist && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UISideNavItem, {
        linkComponent: "div",
        role: "navigation-item",
        title: isHubspotPlaylist && shouldDisplayHubspotPlaylistButtons ? /*#__PURE__*/_jsx(EditHubspotPlaylistButton, {
          playlistId: playlistId,
          style: menuItemStyle,
          use: "unstyled"
        }) : /*#__PURE__*/_jsx(EditPlaylistButton, {
          playlistId: playlistId,
          style: menuItemStyle,
          use: "unstyled"
        })
      }), /*#__PURE__*/_jsx(CardMenuChangePlaylistThumbnailOption, {
        onItemClick: closeModal,
        playlistId: playlistId
      }), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      })]
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: itemToShare || item,
      onShare: closeModal,
      shareButtonLabel: itemToShare ? undefined : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.shareThis.sharePlaylist"
      })
    }), /*#__PURE__*/_jsx(UISideNavItem, {
      linkComponent: "div",
      role: "navigation-item",
      title: /*#__PURE__*/_jsx(AssignPlaylistButton, {
        playlistId: playlistId,
        style: {
          width: '100%',
          fontWeight: 'normal',
          textDecoration: 'none'
        },
        tooltipProps: {
          placement: 'right'
        },
        use: "unstyled"
      })
    }), /*#__PURE__*/_jsx(CardMenuUnassignPlaylistOption, {
      onUnassigned: () => {
        trackUnassignedPlaylist();
        closeModal();
      },
      playlistId: playlistId
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(UISideNavItem, {
      linkComponent: "div",
      role: "navigation-item",
      title: /*#__PURE__*/_jsx(DuplicatePlaylistButton, {
        playlistId: playlistId,
        style: menuItemStyle,
        use: "unstyled"
      })
    }), !isHubspotPlaylist && shouldDisplayHubspotPlaylistButtons && /*#__PURE__*/_jsx(UISideNavItem, {
      linkComponent: "div",
      role: "navigation-item",
      title: /*#__PURE__*/_jsx(CreateHubspotPlaylistButton, {
        playlistId: playlistId
      })
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SavePlaylistButton, {
      onClick: closeModal,
      playlistId: playlistId,
      render: content => /*#__PURE__*/_jsx(UISideNavItem, {
        role: "navigation-item",
        title: content
      }),
      style: {
        width: '100%'
      }
    }), userIsOwner && /*#__PURE__*/_jsx(ArchivePlaylistButton, {
      onClick: closeModal,
      playlistId: playlistId,
      render: content => /*#__PURE__*/_jsx(UISideNavItem, {
        role: "navigation-item",
        title: content
      }),
      showTooltip: true,
      style: {
        width: '100%',
        marginLeft: 0
      }
    })]
  });
};
const ShortMenuOptions = ({
  item,
  itemToShare,
  closeModal
}) => {
  const {
    id: shortId
  } = item;
  const onCreateNewClick = () => {
    trackCreateNewPlaylist(shortId, 'SHORT_FORM_CONTENT');
    closeModal();
  };
  const onAddToPlaylistClick = () => {
    trackAddToPlaylist(shortId, 'SHORT_FORM_CONTENT');
    closeModal();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardMenuWatchLaterOption, {
      item: item,
      onClick: closeModal
    }), /*#__PURE__*/_jsx(CardMenuAddToPlaylistOption, {
      item: item,
      onCreatePlaylistClick: onCreateNewClick,
      onPlaylistClick: onAddToPlaylistClick
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: itemToShare || item,
      onShare: closeModal,
      shareButtonLabel: itemToShare ? undefined : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.shareThis.shareShort"
      })
    }), /*#__PURE__*/_jsx(CopyEmbedCodeMenuOption, {
      language: item.language
    })]
  });
};
const ShortMenuFooter = ({
  item,
  closeModal
}) => {
  const {
    id: shortId
  } = item;
  const onCreateNewClick = () => {
    closeModal();
    trackCreateNewPlaylist(shortId, 'SHORT_FORM_CONTENT');
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsx(CreatePlaylistLink, {
      item: item,
      render: (content, to) => /*#__PURE__*/_jsx(CardMenuFooterLink, {
        onClick: onCreateNewClick,
        to: to,
        children: content
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.cardMenu.addToNewPlaylist"
      })
    })]
  });
};
const DesktopCardMenu = _ref => {
  let {
      children,
      placement = 'bottom right',
      onOpenChange,
      isOpen
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    type
  } = props.item;
  const handleOnOpenChange = e => onOpenChange(e.target.value);
  const closeModal = () => onOpenChange(false);
  return /*#__PURE__*/_jsx(CardMenu, {
    content: {
      body: /*#__PURE__*/_jsxs(CardMenuOptions, {
        children: [(type === 'PLAYLIST' || type === 'LEARNING_PATH') && /*#__PURE__*/_jsx(PlaylistMenuOptions, Object.assign({
          closeModal: closeModal
        }, props)), (type === 'TRACK' || type === 'LESSON') && /*#__PURE__*/_jsx(TrackLessonMenuOptions, Object.assign({
          closeModal: closeModal
        }, props)), type === 'SHORT_FORM_CONTENT' && /*#__PURE__*/_jsx(ShortMenuOptions, Object.assign({
          closeModal: closeModal
        }, props))]
      }),
      footer: type !== 'PLAYLIST' && type !== 'LEARNING_PATH' && /*#__PURE__*/_jsxs(_Fragment, {
        children: [type === 'SHORT_FORM_CONTENT' && /*#__PURE__*/_jsx(ShortMenuFooter, Object.assign({
          closeModal: closeModal
        }, props)), (type === 'TRACK' || type === 'LESSON') && /*#__PURE__*/_jsx(TrackLessonMenuFooter, Object.assign({
          closeModal: closeModal
        }, props))]
      })
    },
    onOpenChange: handleOnOpenChange,
    open: isOpen,
    placement: placement,
    children: children
  });
};
export default DesktopCardMenu;